import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Component, ElementRef, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import {
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { PhoneInterface } from '@app/modules/dialogs/rich-verify-phone-dialog/authenticate-by-phone.service';
import { DeviceService } from '@app/services/device.service';
import { MixpanelService } from '@app/services/mixpanel.service';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { OverlayModule } from '@angular/cdk/overlay';

@Component({
    selector: 'app-dialog-rich-verify',
    templateUrl: './rich-verify-dialog.component.html',
    styleUrls: ['./rich-verify-dialog.component.scss'],
    imports: [
        ReactiveFormsModule,
        OverlayModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
    ],
})
export class RichVerifyDialogComponent implements OnInit {
    dialogRef = inject<MatDialogRef<RichVerifyDialogComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA);

    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly mixpanelService = inject(MixpanelService);
    public readonly deviceService = inject(DeviceService);

    public blurContent = true; // settings for blurring content under the dialog
    activationCode!: string;
    androidShowProcess = false;
    isIphone = false;
    public activationForm!: UntypedFormGroup;
    @ViewChildren('activationInput', { read: ElementRef }) activationInputs!: QueryList<ElementRef>;
    public phone?: PhoneInterface;
    public email?: string;
    public verifiedState = true;
    public verifyApprove = false;
    public submitPromiseWithCode!: Promise<string>;
    private submitPromise!: (code: string) => void;
    public isDesktop: boolean;
    constructor() {
        this.resetDialog();

        this.deviceService.isDesktopObservable.subscribe((isDesktop: boolean) => {
            this.isDesktop = isDesktop;
        });
        this.isIphone = /iphone/i.test(navigator.userAgent);
    }

    get activationCodeForm() {
        return this.activationForm.get('activationCode') as UntypedFormArray;
    }

    ngOnInit() {
        this.createActivationForm();

        this.blurContent = this.data?.blurContent;
        setTimeout(() => {
            this.dialog.afterOpened.next(this.dialogRef);
        }, 0);

        if (this.phone) {
            this.mixpanelService.track('$verification_dialog_login_display', {
                phoneNumber: this.phone.phoneNumber,
                phoneCountryCode: this.phone.phoneCountryCode,
            });
        }
    }

    public resetDialog() {
        this.activationCode = '';

        if (this.activationForm) {
            this.activationCodeForm.controls.forEach((input) => input.reset());
        }
        this.submitPromiseWithCode = new Promise((resolve) => {
            this.submitPromise = resolve;
        });

        setTimeout(() => {
            this.activationInputs.toArray()[0]?.nativeElement.focus();
        }, 0);
    }

    public codeKeyEvent($event: KeyboardEvent | CustomEvent, index: number) {
        if ($event instanceof KeyboardEvent) {
            if ($event.key === 'Backspace') {
                this.activationCodeForm.get(index.toString())?.patchValue('');

                if (index > 0) {
                    this.activationInputs.toArray()[index - 1].nativeElement.focus();
                }
                this.checkActivationCodeLength();
            } else if (
                $event.key === '0' ||
                $event.key === '1' ||
                $event.key === '2' ||
                $event.key === '3' ||
                $event.key === '4' ||
                $event.key === '5' ||
                $event.key === '6' ||
                $event.key === '7' ||
                $event.key === '8' ||
                $event.key === '9'
            ) {
                this.activationCodeForm.get(index.toString())?.patchValue($event.key);
                if (index < 5) {
                    this.activationInputs.toArray()[index + 1].nativeElement.focus();
                }
                this.checkActivationCodeLength();
            }
        } else {
            const activationCode = ($event.target as HTMLInputElement).value;
            if (activationCode.length === 6) {
                for (let x = 0; x < this.activationCodeForm.controls.length; x++) {
                    this.activationCodeForm.get(x.toString())?.patchValue(activationCode[x]);
                }
                this.checkActivationCodeLength();
            }
        }
    }

    public submitDialog() {
        if (this.phone) {
            this.mixpanelService.track('$verification_dialog_login_submit', {
                phoneNumber: this.phone.phoneNumber,
                phoneCountryCode: this.phone.phoneCountryCode,
            });
        }
        if (this.activationCode.length === 6) {
            this.submitPromise(this.activationCode);
        }
    }

    private createActivationForm() {
        this.activationForm = this.formBuilder.group({
            activationCode: this.formBuilder.array([]),
        });

        for (let x = 0; x < 6; x++) {
            const control = new UntypedFormControl([null, Validators.required]);
            this.activationCodeForm.push(control);
        }
    }

    private checkActivationCodeLength() {
        this.activationCode = this.activationCodeForm.controls
            .map((a) => (typeof a.value === 'number' || typeof a.value === 'string' ? +a.value : null))
            .filter((b) => typeof b === 'number')
            .join('');
        this.verifyApprove = !!(this.activationCode && this.activationCode.length === 6);
        if (this.verifyApprove) {
            this.submitDialog();
        }
    }

    public codePasteEvent($event: ClipboardEvent) {
        const activationCode = $event.clipboardData?.getData('Text').trim();

        if (activationCode) {
            for (let x = 0; x < this.activationCodeForm.controls.length; x++) {
                this.activationCodeForm.get(x.toString())?.patchValue(activationCode[x]);
            }

            this.checkActivationCodeLength();
        }

        $event.preventDefault();
    }

    public closeDialog() {
        if (this.phone) {
            this.mixpanelService.track('$verification_dialog_login_cancel', {
                phoneNumber: this.phone.phoneNumber,
                phoneCountryCode: this.phone.phoneCountryCode,
            });
        }
        this.submitPromise(null);
        this.dialogRef.close(false);
    }

    displayAndroidProcess() {
        this.androidShowProcess = !this.androidShowProcess;
        if (this.androidShowProcess) {
            this.mixpanelService.track('$verification_dialog_login_android_help', {
                phoneNumber: this.phone.phoneNumber,
                phoneCountryCode: this.phone.phoneCountryCode,
            });
        }
    }
}
